import type {
  BulletListBlock,
  DialogueBlock,
  NumberedListBlock,
  PrintLinesBlock,
  ReadingPassageBlock,
  TableBlock,
  TaskAnswer,
  TaskBlock,
  TextBlock,
  WordBoxBlock,
} from "@/lib/testTypes";

type BlockRendererProps = {
  block: TaskBlock;
  blockIndex: number;
  answers?: TaskAnswer[];
  showAnswers: boolean;
};

function renderBlankFill(text: string, answer?: string, showAnswers?: boolean) {
  return text.replace(
    /_____+/g,
    showAnswers && answer
      ? `<u class="font-semibold text-green-700">${answer}</u>`
      : '<span class="inline-block w-16 border-b-2 border-gray-400 align-bottom">&nbsp;</span>',
  );
}

function WordBoxTaskBlock({ block }: { block: WordBoxBlock }) {
  return (
    <div className="mb-4 flex flex-wrap gap-x-3 gap-y-1 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
      {block.words.map((word, i) => (
        <span
          key={i}
          className="flex items-center gap-2 text-sm font-medium text-gray-700"
        >
          {word}
          {i < block.words.length - 1 && (
            <span className="select-none text-gray-300">|</span>
          )}
        </span>
      ))}
    </div>
  );
}

function NumberedListTaskBlock({
  block,
  blockIndex,
  answers,
  showAnswers,
}: {
  block: NumberedListBlock;
  blockIndex: number;
  answers?: TaskAnswer[];
  showAnswers: boolean;
}) {
  return (
    <ol className="ml-1 space-y-2">
      {block.items.map((item) => {
        const answer = answers?.find(
          (entry) => entry.blockIndex === blockIndex && entry.itemRef === item.number,
        );
        const rendered = renderBlankFill(item.text, answer?.answer, showAnswers);
        return (
          <li key={item.number} className="flex gap-2 text-sm text-gray-800">
            <span className="w-5 shrink-0 text-gray-500">{item.number}.</span>
            <span dangerouslySetInnerHTML={{ __html: rendered }} />
          </li>
        );
      })}
    </ol>
  );
}

function BulletListTaskBlock({ block }: { block: BulletListBlock }) {
  return (
    <ul className="ml-1 space-y-1">
      {block.items.map((item, i) => (
        <li key={i} className="flex gap-2 text-sm text-gray-800">
          <span className="mt-0.5 shrink-0 text-gray-400">•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function TableTaskBlock({
  block,
  blockIndex,
  answers,
  showAnswers,
}: {
  block: TableBlock;
  blockIndex: number;
  answers?: TaskAnswer[];
  showAnswers: boolean;
}) {
  const hasHeaders = block.columns.some((col) => col !== "");
  return (
    <table className="mt-1 w-full border-collapse text-sm">
      {hasHeaders && (
        <thead>
          <tr>
            {block.columns.map((col, i) => (
              <th
                key={i}
                className="border border-gray-300 bg-gray-50 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-700"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
      )}
      <tbody>
        {block.rows.map((row, rowIdx) => {
          const answer = answers?.find(
            (entry) => entry.blockIndex === blockIndex && entry.itemRef === rowIdx,
          );
          return (
            <tr key={rowIdx} className={rowIdx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
              {row.map((cell, cellIdx) => (
                <td key={cellIdx} className="border border-gray-300 px-3 py-2 text-gray-800">
                  {cell}
                  {showAnswers && answer && cellIdx === row.length - 1 && (
                    <span className="ml-2 text-xs font-medium text-green-600">
                      → {answer.answer}
                    </span>
                  )}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function DialogueTaskBlock({
  block,
  blockIndex,
  answers,
  showAnswers,
}: {
  block: DialogueBlock;
  blockIndex: number;
  answers?: TaskAnswer[];
  showAnswers: boolean;
}) {
  return (
    <div className="ml-1 space-y-2">
      {block.lines.map((line, i) => {
        const answer = answers?.find(
          (entry) => entry.blockIndex === blockIndex && entry.itemRef === i,
        );
        const rendered = renderBlankFill(line.text, answer?.answer, showAnswers);
        return (
          <div key={i} className="flex gap-3 text-sm text-gray-800">
            <span className="w-16 shrink-0 text-right font-semibold text-gray-600">
              {line.speaker}:
            </span>
            <span dangerouslySetInnerHTML={{ __html: rendered }} />
          </div>
        );
      })}
    </div>
  );
}

function TextTaskBlock({ block }: { block: TextBlock }) {
  return <p className="text-sm leading-relaxed text-gray-800">{block.content}</p>;
}

function ReadingPassageTaskBlock({ block }: { block: ReadingPassageBlock }) {
  return (
    <div className="my-3 rounded-r-lg border-l-4 border-[#2A6B4F] bg-gray-50 px-5 py-4">
      <p className="whitespace-pre-wrap text-sm leading-7 text-gray-800">{block.content}</p>
    </div>
  );
}

function PrintLinesTaskBlock({ block }: { block: PrintLinesBlock }) {
  return (
    <div className="mt-4 space-y-4">
      {Array.from({ length: block.count }).map((_, i) => (
        <div key={i} className="h-5 border-b border-gray-300" />
      ))}
    </div>
  );
}

export function BlockRenderer({
  block,
  blockIndex,
  answers,
  showAnswers,
}: BlockRendererProps) {
  switch (block.type) {
    case "word_box":
      return <WordBoxTaskBlock block={block} />;
    case "numbered_list":
      return (
        <NumberedListTaskBlock
          block={block}
          blockIndex={blockIndex}
          answers={answers}
          showAnswers={showAnswers}
        />
      );
    case "bullet_list":
      return <BulletListTaskBlock block={block} />;
    case "table":
      return (
        <TableTaskBlock
          block={block}
          blockIndex={blockIndex}
          answers={answers}
          showAnswers={showAnswers}
        />
      );
    case "dialogue":
      return (
        <DialogueTaskBlock
          block={block}
          blockIndex={blockIndex}
          answers={answers}
          showAnswers={showAnswers}
        />
      );
    case "text":
      return <TextTaskBlock block={block} />;
    case "reading_passage":
      return <ReadingPassageTaskBlock block={block} />;
    case "print_lines":
      return <PrintLinesTaskBlock block={block} />;
    default:
      return null;
  }
}
